import type { NeoDiscountRange, NeoUserStatus, Price, PromotionPrice } from '@expert/types';

export const getValidPromotionUserStatesByCurrentUser = (status: NeoUserStatus): Array<string> => {
    switch (status) {
        case 'ACTIVE':
            return ['ACTIVE', 'ANONYMOUS'];
        case 'ACTIVE_PLUS':
            return ['ACTIVE_PLUS', 'ACTIVE', 'ANONYMOUS'];
        case 'ANONYMOUS':
        default:
            return ['ANONYMOUS'];
    }
};

const prefixPromotionTitles = (title: string, discountRange: NeoDiscountRange | undefined): string => {
    let prefix = '';
    if (discountRange) {
        switch (discountRange.discountType) {
            case 'ABSOLUTE':
                prefix = `${discountRange.discount}€ `;
                break;
            case 'RELATIVE':
                prefix = `${discountRange.discount}% `;
                break;
        }
    }

    return prefix + title;
};

const buildArticlePromotion = (compactPromotion: CompactPromotion, promotionData: CompactPromotionData, price?: Price): ArticlePromotion => {
    const newPromotion: ArticlePromotion = structuredClone({
        ...compactPromotion,
        localizedProperties: {
            ...compactPromotion.localizedProperties,
            teaser: promotionData.teaserMediumId,
        },
        showBubbleOnlyWhenOnlineAvailable: promotionData.showBubbleOnlyWhenOnlineAvailable,
        showBubbleOnlyWhenStoreAvailable: promotionData.showBubbleOnlyWhenStoreAvailable,
        canModifyReservation: promotionData.canModifyReservation,
        discountRange: price ? promotionData.discountRanges?.find(
            (dr) => dr.to > price.bruttoPrice && dr.from < price.bruttoPrice,
        ) : undefined,
        possibleDiscountRange: price ? promotionData.possibleDiscountRanges?.find(
            (dr) => dr.to > price.bruttoPrice && dr.from < price.bruttoPrice,
        ) : undefined,
    });

    if (newPromotion.type === 'DIRECT_DISCOUNT') {
        newPromotion.localizedProperties.title = prefixPromotionTitles(
            newPromotion.localizedProperties.title,
            newPromotion.discountRange,
        );
        newPromotion.localizedProperties.pillTitle = newPromotion.localizedProperties.pillTitle
            ? prefixPromotionTitles(newPromotion.localizedProperties.pillTitle, newPromotion.discountRange)
            : null;
    }

    return newPromotion;
}

export const getPromotionForModificationFromIndex = (index: PromotionIndex | undefined, modificationId: string): ArticlePromotion | undefined => {
    if (!index) {
        useLogger('promotion').warn(`Tried to get promotion by modificationId (${modificationId}) but index is unset.`);
        return undefined;
    }

    const modification: CompactPromotionData | undefined = index.compactPromotionData[modificationId];
    if (modification) {
        const promotion: CompactPromotion | undefined = index.globalPromotions[modification.promotionId];
        if (promotion) {
            return buildArticlePromotion(promotion, modification);
        }
    }

    return undefined;
}

export const getPromotionsForArticleFromIndex = (
    index: PromotionIndex | undefined,
    articleId: string,
    price?: Price,
): Array<ArticlePromotion> => {
    if (!index) {
        useLogger('promotion').warn(`Tried to get promotions for article ${articleId} but index is unset.`);
        return [];
    }

    const promotionDataForAny: Array<string> = index.indexed['ANY'] ?? [];
    const promotionDataIdsForArticle: Array<string> = index.indexed[articleId] ?? [];
    const promotions: Array<ArticlePromotion> = [];

    for (const dataId of promotionDataForAny.concat(promotionDataIdsForArticle)) {
        const data = index.compactPromotionData[dataId];
        if (data) {
            const promo = index.globalPromotions[data.promotionId];
            if (promo) {
                promotions.push(buildArticlePromotion(promo, data, price));
            }
        }
    }

    if (price?.shipmentArray && price?.shipmentArray?.[0]?.shipmentBruttoPrice === 0) {
        promotions.push({
            id: 'VSK',
            priority: -10000,
            type: 'CASHBACK',
            couponType: '',
            localizedProperties: {
                title: 'Versandkostenfrei',
                description: 'Versandkostenfrei',
                pillTitle: 'Versandkostenfrei',
                pillTheme: 'expert-outline',
                teaser: '',
            },
            showInCheckout: true,
            canModifyReservation: true,
            showBubbleOnlyWhenOnlineAvailable: false,
            showBubbleOnlyWhenStoreAvailable: false,
        });
    }

    return promotions.sort((a, b) => a.priority - b.priority);
};

export const getArticlePromotionPriceFromIndex = (index: PromotionIndex | undefined, articleId: string, price: Price): PromotionPrice => {
    const articlePromotions: Array<ArticlePromotion> = getPromotionsForArticleFromIndex(index, articleId, price);
    const promotionPrice: PromotionPrice = {
        currentPrice: price.bruttoPrice,
        directDiscount: 0,
        directDiscountTitle: '',
        checkoutPrice: price.bruttoPrice,
        cashbackDiscount: 0,
        cashbackTitle: '',
        cashbackSummaryTitle: '',
        afterCashbackPrice: price.bruttoPrice,
    };
    let currentCashbackPriority: number = 0;

    for (const articlePromotion of articlePromotions) {
        if (articlePromotion.id === 'VSK') {
            continue;
        }

        if (articlePromotion.type === 'CASHBACK' && (promotionPrice.cashbackDiscount === 0 || (promotionPrice.cashbackDiscount !== 0 && articlePromotion.priority > currentCashbackPriority))) {
            let cashbackDiscountValue: number | undefined;
            if (articlePromotion.possibleDiscountRange?.discountType === 'ABSOLUTE') {
                cashbackDiscountValue = -articlePromotion.possibleDiscountRange.discount;
            } else if (articlePromotion.possibleDiscountRange?.discountType === 'RELATIVE') {
                cashbackDiscountValue = -(
                    (promotionPrice.currentPrice * articlePromotion.possibleDiscountRange.discount)
                    / 100
                );
            }

            if (cashbackDiscountValue) {
                promotionPrice.cashbackDiscount = cashbackDiscountValue;
                promotionPrice.cashbackTitle = articlePromotion.localizedProperties.title;
                promotionPrice.cashbackSummaryTitle =
                    !articlePromotion.showInCheckout && articlePromotion.canModifyReservation
                        ? 'Dein Preis im Markt'
                        : 'Dein Preis nach Cashback';
                currentCashbackPriority = articlePromotion.priority;
            }
        } else {
            if (articlePromotion.discountRange?.discountType === 'ABSOLUTE') {
                promotionPrice.directDiscount = Math.max(
                    -promotionPrice.currentPrice,
                    promotionPrice.directDiscount - articlePromotion.discountRange.discount,
                );
            } else if (articlePromotion.discountRange?.discountType === 'RELATIVE') {
                promotionPrice.directDiscount = Math.max(
                    -promotionPrice.currentPrice,
                    promotionPrice.directDiscount
                        - (promotionPrice.currentPrice * articlePromotion.discountRange.discount) / 100,
                );
            }

            if (promotionPrice.directDiscountTitle === '') {
                promotionPrice.directDiscountTitle = articlePromotion.localizedProperties.title;
            } else {
                promotionPrice.directDiscountTitle = 'sofort-rabatt';
            }
        }
    }

    promotionPrice.checkoutPrice = Math.max(0, promotionPrice.currentPrice + promotionPrice.directDiscount);
    promotionPrice.afterCashbackPrice = Math.max(0, promotionPrice.checkoutPrice + promotionPrice.cashbackDiscount);

    return promotionPrice;
};
